import React, { useState, useEffect, useMemo } from 'react';
import {
  View,
  Text,
  FlatList,
  TouchableOpacity,
  RefreshControl,
} from 'react-native';
import { useRouter } from 'expo-router';
import { Vision, Goal } from '../../../src/api/client';
import { useTeamStore } from '../../../src/stores/teamStore';
import { decryptEntityTitle } from '../../../src/utils/encryption';
import { useTheme } from '../../../src/theme/ThemeContext';
import { useThemedStyles } from '../../../src/theme/useThemedStyles';
import type { Theme } from '../../../src/theme/types';
import { isToday } from '../../../src/utils/format';
import FloatingActionButton from '../../../src/components/FloatingActionButton';
import DataStateView from '../../../src/components/DataStateView';
import LastUpdatedBanner from '../../../src/components/LastUpdatedBanner';
import { useVisionsList } from '../../../src/hooks/queries/useVisionsList';

const createStyles = (theme: Theme) => ({
  container: { flex: 1, backgroundColor: theme.colors.bgSecondary },
  introBar: { backgroundColor: theme.colors.primaryBg, paddingHorizontal: 16, paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: theme.colors.primaryLight },
  introText: { fontSize: 13, color: theme.colors.primary, fontWeight: '500' as const, textAlign: 'center' as const },
  listContent: { padding: 16, paddingBottom: 80 },
  visionCard: { backgroundColor: theme.colors.background, borderRadius: 12, padding: 16, marginBottom: 10, ...theme.shadows.sm, borderLeftWidth: 4, borderLeftColor: theme.colors.primary },
  visionContent: { flex: 1 },
  visionHeader: { flexDirection: 'row' as const, alignItems: 'flex-start' as const, justifyContent: 'space-between' as const, gap: 8, marginBottom: 6 },
  visionTitle: { fontSize: 17, fontWeight: '600' as const, color: theme.colors.textPrimary, lineHeight: 24, flex: 1 },
  defaultBadge: { backgroundColor: theme.colors.primaryBg, paddingHorizontal: 8, paddingVertical: 3, borderRadius: 4 },
  defaultBadgeText: { fontSize: 11, fontWeight: '700' as const, color: theme.colors.primary, textTransform: 'uppercase' as const },
  progressSection: { marginBottom: 8 },
  progressTrack: { height: 6, backgroundColor: theme.colors.bgTertiary, borderRadius: 3, overflow: 'hidden' as const },
  progressFill: { height: '100%' as const, backgroundColor: theme.colors.primary, borderRadius: 3 },
  visionFooter: { flexDirection: 'row' as const, alignItems: 'center' as const, justifyContent: 'space-between' as const },
  goalCountText: { fontSize: 13, color: theme.colors.textDisabled, fontWeight: '500' as const },
  chevron: { fontSize: 22, color: theme.colors.borderMedium, fontWeight: '300' as const },
  emptyContainer: { flexGrow: 1, justifyContent: 'center' as const, alignItems: 'center' as const },
  emptyState: { alignItems: 'center' as const, paddingHorizontal: 40 },
  emptyIcon: { fontSize: 48, marginBottom: 16, color: theme.colors.borderMedium },
  emptyTitle: { fontSize: 20, fontWeight: '700' as const, color: theme.colors.textSecondary, marginBottom: 8 },
  emptySubtitle: { fontSize: 15, color: theme.colors.textDisabled, textAlign: 'center' as const, lineHeight: 22 },
  impactRow: { marginTop: 6, backgroundColor: theme.colors.primaryBg, borderRadius: 6, paddingHorizontal: 10, paddingVertical: 4, alignSelf: 'flex-start' as const },
  impactText: { fontSize: 11, fontWeight: '600' as const, color: theme.colors.primary },
});

export default function VisionScreen() {
  const router = useRouter();
  const styles = useThemedStyles(createStyles);
  const { colors } = useTheme();
  const activeTeamId = useTeamStore((s) => s.activeTeamId);

  const { data, isLoading, error, refetch, isRefetching, dataUpdatedAt, isStale } = useVisionsList();

  const [visionTitles, setVisionTitles] = useState<Record<string, string>>({});

  // Decrypt vision titles when data changes
  useEffect(() => {
    if (!data?.visions) return;
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(
        data.visions.map(async (v) => [v.id, await decryptEntityTitle(v)] as const)
      );
      if (!cancelled) setVisionTitles(Object.fromEntries(entries));
    })();
    return () => { cancelled = true; };
  }, [data?.visions]);

  // Compute derived data from goals and activities
  const { goalCounts, visionProgress, visionImpact } = useMemo(() => {
    if (!data) return { goalCounts: {} as Record<string, { total: number; active: number; completedGoals: number }>, visionProgress: {} as Record<string, number>, visionImpact: {} as Record<string, { connected: number; total: number }> };

    const { visions, goals: goalList, activities: activityList } = data;

    // Build goal counts per vision
    const counts: Record<string, { total: number; active: number; completedGoals: number }> = {};
    const goalsByVision: Record<string, Goal[]> = {};
    goalList.forEach((g) => {
      if (g.vision_id) {
        if (!counts[g.vision_id]) counts[g.vision_id] = { total: 0, active: 0, completedGoals: 0 };
        counts[g.vision_id].total++;
        if (g.status === 'active' || g.status === 'planned') counts[g.vision_id].active++;
        if (g.status === 'completed') counts[g.vision_id].completedGoals++;
        if (!goalsByVision[g.vision_id]) goalsByVision[g.vision_id] = [];
        goalsByVision[g.vision_id].push(g);
      }
    });

    // Calculate overall progress per vision based on activity completion
    const progress: Record<string, number> = {};
    visions.forEach((v: Vision) => {
      const vGoals = goalsByVision[v.id] || [];
      const goalIds = new Set(vGoals.map((g) => g.id));
      const vActivities = activityList.filter((t) => t.goal_id && goalIds.has(t.goal_id));
      if (vActivities.length > 0) {
        const doneActivities = vActivities.filter((t) => t.status === 'done').length;
        progress[v.id] = Math.round((doneActivities / vActivities.length) * 100);
      } else if (vGoals.length > 0) {
        const completedGoals = vGoals.filter((g) => g.status === 'completed').length;
        progress[v.id] = Math.round((completedGoals / vGoals.length) * 100);
      } else {
        progress[v.id] = 0;
      }
    });

    // Compute impact score per vision: today's activities connected
    const todayActivities = activityList.filter((t) => isToday(t.start_time));
    const impact: Record<string, { connected: number; total: number }> = {};
    visions.forEach((v: Vision) => {
      const vGoals = goalsByVision[v.id] || [];
      const goalIds = new Set(vGoals.map((g) => g.id));
      const connected = todayActivities.filter((t) => t.goal_id && goalIds.has(t.goal_id)).length;
      impact[v.id] = { connected, total: todayActivities.length };
    });

    return { goalCounts: counts, visionProgress: progress, visionImpact: impact };
  }, [data]);

  const renderVision = ({ item }: { item: Vision }) => {
    const counts = goalCounts[item.id];
    const progress = visionProgress[item.id] ?? 0;
    const impact = visionImpact[item.id];

    return (
      <TouchableOpacity
        style={styles.visionCard}
        onPress={() => router.push(`/(tabs)/visions/${item.id}` as any)}
        activeOpacity={0.7}
        accessibilityRole="button"
        accessibilityLabel={`${visionTitles[item.id] || 'Untitled'}${counts ? `, ${counts.total} ${counts.total === 1 ? 'goal' : 'goals'}, ${progress}% complete` : ', no goals linked'}`}
        testID={`visions.vision-card-${item.id}`}
      >
        <View style={styles.visionContent} accessible={false}>
          <View style={styles.visionHeader} accessible={false}>
            <Text style={styles.visionTitle} numberOfLines={2} accessible={false}>
              {'\u2728'} {visionTitles[item.id] || 'Untitled'}
            </Text>
          </View>

          {/* Progress bar */}
          {counts && counts.total > 0 && (
            <View style={styles.progressSection} accessible={false}>
              <View style={styles.progressTrack} accessible={false}>
                <View style={[styles.progressFill, { width: `${progress}%` }]} accessible={false} />
              </View>
            </View>
          )}

          <View style={styles.visionFooter} accessible={false}>
            {counts ? (
              <Text style={styles.goalCountText} accessible={false}>
                {counts.total} {counts.total === 1 ? 'Goal' : 'Goals'} {'\u00B7'} {progress}% complete
              </Text>
            ) : (
              <Text style={styles.goalCountText} accessible={false}>No goals linked</Text>
            )}
            <Text style={styles.chevron} accessible={false}>{'\u203A'}</Text>
          </View>
          {impact && impact.total > 0 && (
            <View style={styles.impactRow} accessible={false}>
              <Text style={styles.impactText} accessible={false}>
                Today: {impact.connected}/{impact.total} activities
              </Text>
            </View>
          )}
        </View>
      </TouchableOpacity>
    );
  };

  const visions = data?.visions ?? [];

  return (
    <DataStateView
      isLoading={isLoading}
      error={error}
      hasData={!!data}
      onRetry={refetch}
      entityName="visions"
      testIDPrefix="visions"
    >
    <View style={styles.container}>
      <LastUpdatedBanner dataUpdatedAt={dataUpdatedAt} isStale={isStale} />
      {/* Intro text */}
      <View style={styles.introBar}>
        <Text style={styles.introText}>Your long-term aspirations that guide your goals.</Text>
      </View>
      <FlatList
        data={visions}
        keyExtractor={(item) => item.id}
        renderItem={renderVision}
        contentContainerStyle={visions.length === 0 ? styles.emptyContainer : styles.listContent}
        refreshControl={
          <RefreshControl
            refreshing={isRefetching}
            onRefresh={() => refetch()}
            colors={[colors.primary]}
            tintColor={colors.primary}
          />
        }
        ListEmptyComponent={
          <View style={styles.emptyState}>
            <Text style={styles.emptyIcon}>{'\u25C9'}</Text>
            <Text style={styles.emptyTitle}>No visions yet</Text>
            <Text style={styles.emptySubtitle}>
              Visions represent your long-term aspirations. Tap + to get started.
            </Text>
          </View>
        }
      />
      <FloatingActionButton onPress={() => router.push('/(tabs)/visions/create')} accessibilityLabel="Create new vision" />
    </View>
    </DataStateView>
  );
}
